export interface Experiment {
  id: string;
  /**
   * @remarks Many experiments use the standard control-available variant system,
   * but some experiments use other custom strings as variant names.
   */
  variant: ExperimentVariant | string;
  /**
   * @deprecated use variant
   */
  group?: string;
  payload?: ExperimentPayloadsType;
}

export interface PayloadByExperimentId {
  FeaturedWalletOffers: {
    featuredOfferIds: string[];
  };
  WalletOfferUrls: {
    walletOfferUrls: Record<string, string>;
  };
  WalletOfferCopies: {
    offerName?: string;
    walletOfferCopies?: Record<string, string>;
  };
  "lloyds-maintenance-copy": {
    title?: string;
    subtitle?: string;
  };
  EnhancedEarnPointsBackRate: {
    pointsBackRates: [{ rate: "default" | number; startDate: string }];
  };
}

export type ExperimentPayloadsType = {
  [K in keyof PayloadByExperimentId]?: PayloadByExperimentId[K];
};

export type Payload = PayloadByExperimentId[keyof PayloadByExperimentId];

export type ExperimentsType = { [id: string]: ExperimentVariant | string };
export type TrackingPropertiesType = {
  [key: string]: string;
};

export interface ExperimentState {
  experiments: ExperimentsType;
  experimentPayloads?: ExperimentPayloadsType;
  trackingProperties?: TrackingPropertiesType;
  /**
   * `true` while the experiments fetch is in flight; `false` after
   * the fetch resolves (success or failure) or when no fetch is
   * configured. Lets consumers distinguish "experiment not yet
   * loaded" from "experiment loaded and absent" without relying on
   * empty-object heuristics or timeouts.
   */
  isLoading?: boolean;
  /**
   * `true` once the experiment values have settled: at least one
   * fetch completed (success or failure), or the provider was
   * pre-seeded via `initState` and no fetch will be issued. Unlike
   * `isLoading`, which is `false` both before a fetch starts and
   * after it settles, this is a monotonic signal per provider
   * lifetime. Gates that must not act on a not-yet-loaded flag
   * (e.g. the NU-470 post-login portal routing decision) should wait
   * on this rather than on `!isLoading`. Caveats: it stays `false`
   * while the user is logged out with no seeded values (no fetch is
   * configured then), and it does not re-arm when a session rotates
   * mid-lifetime; pair it with `refreshNonce` if fresh-per-session
   * values are required.
   */
  isFetched?: boolean;
}

export interface ActiveExperimentsRequest {
  partnerExperiments: string;
}

export interface ActiveExperimentsResponse {
  experiments: Experiment[];
  trackingProperties: TrackingPropertiesType;
}

export enum ExperimentVariant {
  /**
   * @description The "on" state
   */
  Available = "available",
  /**
   * @description The "off" state
   */
  Control = "control",
}

export enum RenderLodgingShopValuePropsVariant {
  Control = "Control",
  Installments = "Installments",
  PriceMatch = "PriceMatch",
  InstallmentsAndPriceMatch = "InstallmentsAndPriceMatch",
}

export enum RenderExtraHotelAmenitiesVariant {
  Control = "Control",
  BreakfastOnly = "BreakfastOnly",
  ExtraAmenities = "ExtraAmenities",
}

export enum RoomDetailsCtaCopyVariant {
  Control = "Control",
  Continue = "Continue",
  ContinueWithThisRoom = "ContinueWithThisRoom",
  ContinueToPayment = "ContinueToPayment",
  Reserve = "Reserve",
}
